import * as React from "react";
import { useDialog } from "@shared-ui/dialog-context";
import { UitkCard, UitkCardContentSection, UitkCardLink } from "uitk-react-cards";
import { CampaignByIdQuery, CampaignRecommendationsWithOffersQuery } from "../../../../../../__generated__/api";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkLayoutPosition } from "uitk-react-layout-position";
import { UitkIcon, UitkIconSize } from "uitk-react-icons";
import { locKeys } from "components/shared/Merchandising/Inventory/l10n";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkPriceLockup, UitkPriceLockupAlign } from "uitk-react-price-lockup";
import { InfoDialog } from "../../Common/InfoDialog/InfoDialog";
import { UitkSpacing } from "uitk-react-spacing";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkBadge } from "uitk-react-badge";
import { UitkHeading, UitkText } from "uitk-react-text";
import Offers = CampaignRecommendationsWithOffersQuery.Offers;
import ActivityOfferItemInlineFragment = CampaignByIdQuery.ActivityOfferItemInlineFragment;

export interface ActivityInventoryCardProps {
  offer: Offers;
  cardIndex: number;
  campaignId: string;
  moduleName: string;
  border?: boolean;
}
export const ActivityInventoryCard = ({
  offer,
  cardIndex,
  campaignId,
  moduleName,
  border,
}: ActivityInventoryCardProps) => {
  const { formatText } = useLocalization();
  const [, dialogActions, ActivityDialogComponent] = useDialog(`disclaimer-info-${campaignId}-${cardIndex}`);

  if (offer.__typename !== "ActivityOfferItem") {
    return null;
  }

  const strikeThroughPrice = offer?.activityOffer?.strikeThroughPrice?.value;
  const currentPrice = offer.activityOffer?.price?.value;
  const a11yPrice = offer.activityOffer?.price?.allyText || "";
  const priceDisclaimerLabel = formatText(locKeys.priceDisclaimerLabel);

  return (
    <UitkCard className="ActivityInventoryCard" border={border} overflow={false}>
      <UitkLayoutFlex direction="column" justifyContent="end">
        <UitkLayoutFlexItem shrink={0} grow={0}>
          <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
            <LazyLoadedImage src={offer.image.url} alt={offer.image.description} />
          </UitkFigure>
        </UitkLayoutFlexItem>
        <UitkLayoutFlexItem grow={1}>
          <UitkLayoutFlex direction="column" justifyContent="space-around">
            <UitkLayoutFlexItem shrink={0} grow={1} alignSelf="start">
              <UitkCardContentSection>
                <div>
                  <UitkSpacing padding={{ blockend: "half" }}>
                    <UitkHeading tag="h3" size={5}>
                      {offer.title}
                    </UitkHeading>
                  </UitkSpacing>
                  <span className="uitk-type-300">{offer.city}</span>
                </div>
              </UitkCardContentSection>
            </UitkLayoutFlexItem>
            <UitkCardContentSection>
              <div>
                <DiscountBadge offer={offer} />
              </div>
              <UitkLayoutFlexItem grow={1} shrink={0}>
                <UitkLayoutGrid alignItems="end" columns={2} space="two">
                  <>
                    <div>
                      <FreeCancellation offer={offer} />
                      <UitkLayoutPosition type="relative">
                        <div className="uitk-type-200">
                          <Duration offer={offer} />
                        </div>
                      </UitkLayoutPosition>
                    </div>
                    <UitkLayoutPosition position={{ zindex: "layer2" }}>
                      <UitkPriceLockup
                        align={UitkPriceLockupAlign.RIGHT}
                        lockupPrice={currentPrice}
                        priceSubtextFirst={offer.activityOffer?.price?.qualifierText}
                        a11yPrice={a11yPrice}
                        a11yStrikeThroughDialogTrigger={priceDisclaimerLabel}
                        strikeThroughPrice={strikeThroughPrice}
                        strikeThroughTrigger={
                          <button className="disclaimerInfoIcon" onClick={dialogActions.openDialog}>
                            <UitkIcon
                              name="info_outline"
                              size={UitkIconSize.SMALL}
                              id={`disclaimer-info-${campaignId}-${cardIndex}`}
                              title={formatText("merchCampaign.disclaimerIcon.title")}
                            />
                          </button>
                        }
                      />
                      <ActivityDialogComponent>
                        <InfoDialog
                          infoText={offer?.activityOffer?.price.disclaimerText}
                          closeDialog={dialogActions.closeDialog}
                          buttonText={formatText("DestinationsByAffinity.priceDialogCloseButton")}
                        />
                      </ActivityDialogComponent>
                    </UitkLayoutPosition>
                  </>
                </UitkLayoutGrid>
              </UitkLayoutFlexItem>
            </UitkCardContentSection>
            <UitkCardLink>
              {offer.activityOffer && (
                <TrackedLink
                  moduleName={moduleName}
                  action={Action.CLICK}
                  href={offer.activityOffer?.actionLink.href}
                  rfrr={`card-${campaignId}-${cardIndex}`}
                >
                  {offer.activityOffer?.actionLink.text}
                </TrackedLink>
              )}
            </UitkCardLink>
          </UitkLayoutFlex>
        </UitkLayoutFlexItem>
      </UitkLayoutFlex>
    </UitkCard>
  );
};

type OfferProps = Pick<ActivityInventoryCardProps, "offer">;

const FreeCancellation: React.FC<OfferProps> = (props) => {
  const offer = props.offer as ActivityOfferItemInlineFragment;

  if (!offer.freeCancellationMessage) return null;

  return (
    <UitkSpacing padding={{ blockend: "two" }}>
      <UitkText theme="positive" size={300} weight="bold">
        {offer?.freeCancellationMessage}
      </UitkText>
    </UitkSpacing>
  );
};

const Duration: React.FC<OfferProps> = (props) => {
  const offer = props.offer as ActivityOfferItemInlineFragment;
  if (offer?.duration) {
    return (
      <UitkLayoutFlexItem className="uitk-type-200">
        <UitkLayoutFlex alignItems="center">
          <UitkSpacing margin={{ inlineend: "one" }}>
            <span>{offer?.durationLabel}</span>
          </UitkSpacing>
          <UitkSpacing margin={{ inlineend: "one" }}>
            <UitkIcon name="access_time" size={UitkIconSize.EXTRA_SMALL} id="access_time" />
          </UitkSpacing>
          <span>{offer?.duration}</span>
        </UitkLayoutFlex>
      </UitkLayoutFlexItem>
    );
  }
  return null;
};

const DiscountBadge: React.FC<OfferProps> = (props) => {
  const offer = props.offer as ActivityOfferItemInlineFragment;
  const discountBadge = offer.badge?.text;
  if (!discountBadge) {
    return (
      <UitkSpacing padding={{ blockend: "six" }} margin={{ block: "unset" }}>
        <hr />
      </UitkSpacing>
    );
  }
  return (
    <>
      <UitkSpacing margin={{ block: "unset" }}>
        <hr className="separatorLineWidth" />
      </UitkSpacing>
      <UitkLayoutPosition type="relative" position={{ top: "minus-three" }}>
        <div className="uitk-type-right">
          <UitkBadge text={discountBadge} />
        </div>
      </UitkLayoutPosition>
    </>
  );
};
